import { FC, useEffect, useRef, useState } from 'react';

type CurrencyType = 'USD' | 'GBP' | 'EUR';

type PlacementType = 'cart' | 'home' | 'category' | 'product' | 'payment';

interface Props {
  amount?: number; // in subunits (e.g. cents)
  fractionDigits?: number;
  currency?: CurrencyType;
  placement?: PlacementType;
  style?: any;
  classNames?: string;
}

/**
 * We load our own messages-only PayPal SDK under a private namespace instead of sharing
 * `window.paypal`.
 *
 * Sharing that global is what originally broke this banner on the cart: Braintree's express
 * checkout installs its own SDK into `window.paypal` mid-session, so any code that read that
 * global — or raced a second script against it — ended up with a stale figure or a banner that
 * vanished on a quantity change. With `data-namespace` the SDK attaches to
 * `window[BNPL_SDK_NAMESPACE]` and nothing else on the page can replace it.
 */
const BNPL_SDK_NAMESPACE = 'paypalBnplSdk';

type PayPalMessagesSdk = {
  Messages: (config?: Record<string, unknown>) => {
    render: (target: string | HTMLElement) => unknown;
  };
};

const readSdk = (): PayPalMessagesSdk | null => {
  if (typeof window === 'undefined') return null;
  const sdk = (window as unknown as Record<string, unknown>)[BNPL_SDK_NAMESPACE] as
    | PayPalMessagesSdk
    | undefined;
  return typeof sdk?.Messages === 'function' ? sdk : null;
};

/**
 * The messages SDK (and the two PayPal iframes it creates) cost ~500ms of main-thread time on a
 * mid-range phone. The Pay Later line is supplementary, so it waits until the page has loaded and
 * the main thread is idle rather than competing with the PDP becoming interactive.
 *
 * `load` waits on every third-party resource (GTM, HubSpot, Clarity, the Meta pixel), and one that
 * hangs would hold the message back forever, so the wait is capped.
 */
const MAX_IDLE_WAIT_MS = 5000;

const afterPageIsIdle = (): Promise<void> =>
  new Promise((resolve) => {
    const cap = setTimeout(resolve, MAX_IDLE_WAIT_MS);
    const done = () => {
      clearTimeout(cap);
      resolve();
    };
    const whenIdle = () =>
      'requestIdleCallback' in window ? window.requestIdleCallback(done, { timeout: 3000 }) : setTimeout(done, 1500);
    if (document.readyState === 'complete') whenIdle();
    else window.addEventListener('load', whenIdle, { once: true });
  });

/** One in-flight load, shared by every banner on the page. */
let sdkLoadPromise: Promise<PayPalMessagesSdk | null> | null = null;

const loadBnplSdk = (clientId: string, currency: CurrencyType): Promise<PayPalMessagesSdk | null> => {
  const existing = readSdk();
  if (existing) return Promise.resolve(existing);
  if (sdkLoadPromise) return sdkLoadPromise;

  sdkLoadPromise = afterPageIsIdle().then(() => new Promise((resolve) => {
    const script = document.createElement('script');
    const params = new URLSearchParams({
      'client-id': clientId,
      components: 'messages',
      currency,
      'enable-funding': 'paylater',
    });
    script.src = `https://www.paypal.com/sdk/js?${params.toString()}`;
    // Keeps the SDK off window.paypal so Braintree's instance and ours coexist.
    script.setAttribute('data-namespace', BNPL_SDK_NAMESPACE);
    script.async = true;
    script.onload = () => resolve(readSdk());
    script.onerror = () => {
      // Allow a later mount to retry rather than caching the failure forever.
      sdkLoadPromise = null;
      console.warn('PayPalBNPLMessages: BNPL SDK failed to load.');
      resolve(null);
    };
    document.head.appendChild(script);
  }));

  return sdkLoadPromise;
};

/** How long the amount must hold still before a render is worth starting — it settles over a few
 *  renders as variant price, finish upcharge and quantity resolve. */
const SETTLE_DELAY_MS = 250;

/**
 * BNPL messaging for PDP and Cart pages.
 *
 * One mechanism for every render: `sdk.Messages(config).render(element)` against a single stable
 * div, re-invoked whenever the amount (or placement/style) changes. The SDK updates the message on
 * that element in place — this is the same call its own attribute-observer makes internally, just
 * without depending on the observer.
 *
 * Hard-won constraints, each of which broke a previous version — do not reintroduce them:
 *
 * - No `window.paypal`. Braintree replaces it mid-session on the cart (see BNPL_SDK_NAMESPACE).
 * - No `data-pp-message` auto-discovery. The SDK's page scan only runs at script load, so banners
 *   mounted via client-side navigation were never discovered: F5 showed the message, normal
 *   navigation showed nothing. Explicit `render()` works identically on both entry paths.
 * - No relying on attribute mutation for updates. The observer did not fire reliably for
 *   quantity changes on the cart; an explicit render per amount change is deterministic.
 * - No remounting the container (`key={amount}` upstream) and no hand-rolled slot/overlay
 *   lifecycle. Both produced blank frames or stale figures; the container must stay put and the
 *   SDK must own what happens inside it.
 */
const PayPalBNPLMessages: FC<Props> = ({
  amount,
  fractionDigits = 2,
  currency = 'USD',
  placement = 'product',
  style = { layout: 'text', logo: { type: 'primary' } },
  classNames = '',
}) => {
  const clientId = process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID;
  const containerRef = useRef<HTMLDivElement>(null);

  // Once a message has rendered, hold its height as a floor so the box cannot collapse (and shove
  // the page around) while an update paints.
  const [minHeight, setMinHeight] = useState(0);

  const numericAmount = amount !== undefined ? amount / Math.pow(10, fractionDigits) : undefined;
  const styleKey = JSON.stringify(style);

  useEffect(() => {
    if (!clientId || numericAmount === undefined) return;

    let cancelled = false;

    const settle = setTimeout(() => {
      loadBnplSdk(clientId, currency).then((sdk) => {
        const container = containerRef.current;
        if (cancelled || !sdk || !container) return;

        const currentHeight = container.offsetHeight;
        if (currentHeight > 0) setMinHeight((prev) => (currentHeight > prev ? currentHeight : prev));

        try {
          sdk
            .Messages({
              amount: numericAmount,
              currency,
              placement,
              style: JSON.parse(styleKey),
            })
            .render(container);
        } catch (err) {
          console.warn('PayPalBNPLMessages: render failed:', err);
        }
      });
    }, SETTLE_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(settle);
    };
  }, [clientId, numericAmount, currency, placement, styleKey]);

  if (!clientId) return null;

  return (
    <div
      ref={containerRef}
      className={`paypal-bnpl-messaging ${classNames}`}
      style={{ minHeight: minHeight ? `${minHeight}px` : undefined }}
    />
  );
};

export default PayPalBNPLMessages;
